<template>
  <div class="flex items-center q-gutter-x-sm">
    <PlatformDropdown
      v-for="platform in platformsWithCategories"
      :key="platform.id"
      :platform="platform"
      :categories="categoriesForPlatform(platform.id)"
      :is-active="isActive(platform.id)"
      :all-selected="isAllPostsSelected(platform.id)"
      :selected-category-ids="getSelectedCategoryIds(platform.id)"
      :selected-lifecycle-statuses="lifecycleStatuses"
      @toggle-platform="togglePlatform(platform.id)"
      @toggle-all-categories="toggleAllPosts(platform.id)"
      @toggle-category="toggleCategory($event, platform.id)"
      @lifecycle-change="$emit('lifecycle-change', $event)"
    />
    <PlatformToggleButton
      v-for="platform in platformsWithoutCategories"
      :key="platform.id"
      :platform="platform"
      :is-active="isActive(platform.id)"
      @toggle="togglePlatform(platform.id)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { HasTaskMixin } from '@/mixins';
import { SocialSite, PostCategory, LifecycleStatus } from '@/type';
import siteRepository from '@/social/site/site-repository';
import categoryRepository from '../category/post-category-repository';
import { PlatformEntry, PlatformCategoriesMap } from '../category/types';
import PlatformDropdown from '../category/PlatformDropdown.vue';
import PlatformToggleButton from '../category/PlatformToggleButton.vue';

interface Data {
  socialSites: SocialSite[],
  categories: PostCategory[],
}

export default defineComponent({
  emits: ['change', 'lifecycle-change'],
  mixins: [HasTaskMixin],
  components: { PlatformDropdown, PlatformToggleButton },
  props: {
    categoryIds: {
      type: Object as () => PlatformCategoriesMap,
      default: () => ({} as PlatformCategoriesMap),
    },
    lifecycleStatuses: {
      type: Array as () => LifecycleStatus[] | null,
      default: null,
    },
  },
  data(): Data {
    return {
      socialSites: [],
      categories: [],
    };
  },
  computed: {
    platformsWithCategories(): SocialSite[] {
      return this.socialSites.filter(platform => this.hasCategoriesForPlatform(platform.id));
    },
    platformsWithoutCategories(): SocialSite[] {
      return this.socialSites.filter(platform => !this.hasCategoriesForPlatform(platform.id));
    },
    activePlatformCount(): number {
      return this.socialSites.filter(platform => this.isActive(platform.id)).length;
    },
  },
  methods: {
    async load() {
      this.task = Promise.all([
        siteRepository.findAll(),
        categoryRepository.findAll(),
      ]);
      this.task.then(([sites, categories]) => {
        this.socialSites = sites;
        this.categories = categories;
        this.initialize();
      });
    },
    hasCategoriesForPlatform(platformId: number): boolean {
      return this.categories.some(c => c.platformId === platformId);
    },
    categoriesForPlatform(platformId: number): PostCategory[] {
      return this.categories.filter(c => c.platformId === platformId);
    },
    isActive(platformId: number): boolean {
      const { all, categoryIds } = this.categoryIds[platformId]
        || { all: false, categoryIds: [] };
      return all || !!categoryIds.length;
    },
    isAllPostsSelected(platformId: number): boolean {
      const { all } = this.categoryIds[platformId] || { all: false };
      return all;
    },
    getSelectedCategoryIds(platformId: number): number[] {
      const entry = this.categoryIds[platformId];
      return entry ? entry.categoryIds : [];
    },
    togglePlatform(platformId: number) {
      if (this.isActive(platformId) && this.activePlatformCount <= 1) {
        return;
      }
      let pState = this.categoryIds[platformId];
      if (this.isActive(platformId)) {
        pState = { all: false, categoryIds: [] };
      } else {
        pState = { all: true, categoryIds: [] };
      }
      const categoryIds = {
        ...this.categoryIds,
        [platformId]: pState,
      };
      this.emitChange({ categoryIds });
    },
    toggleAllPosts(platformId: number) {
      const { all, categoryIds } = this.categoryIds[platformId] || { all: false, categoryIds: [] };
      const updatedCategoryIds = {
        ...this.categoryIds,
        [platformId]: { all: !all, categoryIds },
      };
      this.emitChange({ categoryIds: updatedCategoryIds });
    },
    toggleCategory(categoryId: number, platformId: number) {
      const pState = this.categoryIds[platformId];
      let newCategoryIds: number[];
      if (pState.all) {
        const allCategoryIds = this.categoriesForPlatform(platformId).map(c => c.id);
        newCategoryIds = allCategoryIds.filter(id => id !== categoryId);
      } else {
        newCategoryIds = pState.categoryIds.includes(categoryId)
          ? pState.categoryIds.filter(id => id !== categoryId)
          : [...pState.categoryIds, categoryId];
      }
      const categoryIds = {
        ...this.categoryIds,
        [platformId]: {
          all: false,
          categoryIds: newCategoryIds,
        },
      };
      this.emitChange({ categoryIds });
    },
    emitChange(state?: {categoryIds: PlatformCategoriesMap}) {
      const categoryIds = (state && state.categoryIds) || this.categoryIds;
      this.$emit('change', {
        socialSiteIds: Object.keys(categoryIds).map(Number),
        categoryIds,
      });
    },
    initialize() {
      const categoryIds: Record<number, PlatformEntry> = {};
      this.socialSites.forEach((platform) => {
        categoryIds[platform.id] = this.categoryIds[platform.id] || { all: true, categoryIds: [] };
      });
      this.emitChange({ categoryIds });
    },
  },
  created() {
    this.load();
  },
});
</script>
